import { isEqual } from 'lodash-es';
import React, {
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { shallow } from 'zustand/shallow';

import { Icon } from '@column/column-ui-kit';
import { Button, DataTable, type DataTableColumnDef } from '@column/ui';

import { ReportDownloadMenu } from '~/components/ReportDownloadMenu';
import { type EmptyProps, Empty as TableEmpty, Pagination as TablePagination, tableDefaults } from '~/components/Table';
import { useQueryParams } from '~/hooks';
import { ReportsFilterType, useReporting } from '~/hooks/useReporting';
import {
  TableBankAccountFilter,
  TableDateFilter,
  TableMonthFilter,
  TableSelectFilter,
  type TableSelectFilterOption,
} from '~/lib/components/table';
import { ReportingListParams } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ReportingSettlementReport } from '~/typings/API';
import { MonthBoundariesType, getMonthBoundaries } from '~/util/dateFilterHelpers';
import { shiftDate } from '~/util/reporting';

import { Loading } from './Loading';

export interface ReportingTableProps {
  tableId: string;
  bankAccountId?: string;
  className?: string;
  empty?: EmptyProps;
  action?: ReactNode;
  onRowClick?: (entry: ReportingSettlementReport) => void;
  filter?: ReportsFilterType;
  statementType?: string;
  typeOptions?: TableSelectFilterOption[];
  hideAccountFilter?: boolean;
  columns: (options: {
    timeFormat: TableTimeFormat;
    onTimeFormatChange: (value: TableTimeFormat) => void;
  }) => DataTableColumnDef<ReportingTableRow>[];
  onEntriesChange?: (entries: ReportingSettlementReport[]) => void;
  isLoadingRows?: boolean;
  exactDateFilter?: boolean;
}

export interface ReportingTableRefProps {
  fetch: (params?: ReportsFilterType) => void;
  reset: () => void;
  setQueryParams: (object?: ReportsFilterType) => void;
}

export type ReportingTableRow = Record<string, any> & { entry: ReportingSettlementReport };

const MONTH_FILTER_START = new Date(2022, 0, 1);

export const ReportingTable = forwardRef<ReportingTableRefProps, PropsWithChildren<ReportingTableProps>>(
  (
    {
      bankAccountId,
      filter,
      action,
      columns: getColumns,
      onEntriesChange,
      isLoadingRows = false,
      exactDateFilter = false,
      statementType,
      typeOptions,
      hideAccountFilter = false,
      ...props
    },
    ref
  ) => {
    const { updateSettings } = useSessionStore();
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
    const { timeFormat, update: updateTableConfig } = useTableConfig(props.tableId);

    const { queryParams: initialUrlParams } = useQueryParams<{ type?: string }>();
    const [typeFilterData, setTypeFilterData] = useState<string | undefined>(() =>
      typeOptions ? initialUrlParams?.type : undefined
    );
    const effectiveType = statementType ?? typeFilterData;

    const defaultFilters = useMemo(() => {
      const filters: ReportsFilterType = { ...filter };

      if (effectiveType) {
        filters.type = effectiveType as ReportsFilterType['type'];
        filters.category = undefined;
      }

      return filters;
    }, [filter, effectiveType]);

    const { response, queryParams, isLoading, isInitialLoad, setQueryParams } = useReporting({
      onInitialLoad: () => setIsMounted(true),
      initialParams: defaultFilters,
      persistParams: bankAccountId
        ? {
            statementSubjectId: bankAccountId,
          }
        : undefined,
    });

    const { queryParams: urlQueryParams } = useQueryParams<ReportingListParams>();

    const [filterPeriodData, setFilterPeriodData] = useState<Partial<MonthBoundariesType>>({});
    const isFilterPeriodActive = useMemo(() => {
      if (filterPeriodData?.fromDate || filterPeriodData?.toDate) {
        return true;
      }
      return false;
    }, [filterPeriodData]);

    const [bankAccountIdFilterData, setBankAccountIdFilterData] = useState<string | undefined>(bankAccountId);

    const isFilterActive = useMemo(
      () =>
        Boolean(typeFilterData) ||
        (!bankAccountId && !hideAccountFilter ? isFilterPeriodActive || bankAccountIdFilterData : isFilterPeriodActive),
      [isFilterPeriodActive, bankAccountIdFilterData, bankAccountId, hideAccountFilter, typeFilterData]
    );

    const handleFilterReset = useCallback(() => {
      setFilterPeriodData({});
      setBankAccountIdFilterData(undefined);
      setTypeFilterData(undefined);

      setQueryParams(
        {
          limit: queryParams?.limit,
          page: 1,
          ...defaultFilters,
        },
        true
      );
    }, [queryParams, defaultFilters]);

    useLayoutEffect(() => {
      if (!urlQueryParams) {
        return;
      }

      const filterPeriodDataCompare: Partial<MonthBoundariesType> = {
        toDate: urlQueryParams?.toDate,
        fromDate: urlQueryParams?.fromDate,
      };

      if (!isEqual(filterPeriodData, filterPeriodDataCompare)) {
        setFilterPeriodData(filterPeriodDataCompare);
      }

      if (filter?.category === 'statement' && !bankAccountId) {
        setBankAccountIdFilterData(urlQueryParams.statementSubjectId);
      }
    }, []);

    useEffect(() => {
      const newQueryParams: ReportsFilterType = {
        ...queryParams,
        ...filterPeriodData,
      };

      if (!bankAccountId) {
        newQueryParams.statementSubjectId = hideAccountFilter ? undefined : bankAccountIdFilterData;
      }

      if (statementType || typeOptions) {
        newQueryParams.type = defaultFilters.type;
        newQueryParams.category = defaultFilters.category;
      }

      const queryParamsCompare = { ...queryParams };

      if (!isInitialLoad && !isEqual(newQueryParams, queryParamsCompare)) {
        setQueryParams({
          ...newQueryParams,
          startingAfter: undefined,
          endingBefore: undefined,
          page: 1,
        });

        return;
      }

      setQueryParams({ ...newQueryParams, ...defaultFilters });
    }, [
      filterPeriodData,
      bankAccountIdFilterData,
      defaultFilters,
      bankAccountId,
      statementType,
      typeOptions,
      hideAccountFilter,
    ]);

    useLayoutEffect(() => {
      onEntriesChange?.(response?.reports ?? []);
    }, [response?.reports]);

    const handleTimeFormatChange = useCallback(
      (value: TableTimeFormat) => {
        updateTableConfig({ timeFormat: value }).catch(() => {
          addDangerNotification({ content: 'Failed to save time format preference' });
        });
      },
      [addDangerNotification, updateTableConfig]
    );

    const columns: DataTableColumnDef<ReportingTableRow>[] = [
      ...getColumns({ timeFormat, onTimeFormatChange: handleTimeFormatChange }),
      {
        id: 'action',
        header: '',
        accessor: 'entry',
        width: 'max-content',
        className: 'justify-end',
        cell: ({ row }) => <ReportDownloadMenu report={row.entry} size="xs" variant="secondary" />,
      },
    ];

    const handlePrev = useCallback(() => {
      setQueryParams({
        startingAfter: undefined,
        endingBefore: Number(queryParams?.page) - 1 === 1 ? undefined : response?.reports?.at(0)?.id,
        page: Number(queryParams?.page ?? 1) - 1,
        ...defaultFilters,
      });
    }, [queryParams, response?.reports, defaultFilters]);

    const handleNext = useCallback(() => {
      setQueryParams({
        startingAfter: response?.reports && Array.isArray(response?.reports) ? response.reports?.at(-1)?.id : undefined,
        endingBefore: undefined,
        page: Number(queryParams?.page ?? 1) + 1,
        ...defaultFilters,
      });
    }, [queryParams, response?.reports, defaultFilters]);

    useImperativeHandle(
      ref,
      () => ({
        setQueryParams,
        reset: () => {
          setQueryParams(
            {
              limit: queryParams?.limit,
              startingAfter: undefined,
              endingBefore: undefined,
              page: 1,
              ...defaultFilters,
            },
            true
          );
        },
        fetch: (params?: ReportsFilterType) => {
          setQueryParams({
            ...queryParams,
            ...params,
            ...defaultFilters,
          });
        },
      }),
      [queryParams, defaultFilters]
    );

    useEffect(
      () =>
        useSessionStore.subscribe(
          (state) =>
            !state.settings.tableSettings || !state.settings.tableSettings[props.tableId]
              ? 10
              : state.settings.tableSettings[props.tableId].limit,
          (limit) => {
            if (limit !== queryParams?.limit) {
              setQueryParams({
                limit,
                ...defaultFilters,
              });
            }
          },
          {
            fireImmediately: true,
          }
        ),
      [queryParams]
    );

    useEffect(
      () =>
        useSessionStore.subscribe(
          (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
          () => {
            if (isMounted) {
              return;
            }

            handleFilterReset();
          },
          {
            equalityFn: shallow,
          }
        ),
      [isMounted]
    );

    const rows = useMemo<ReportingTableRow[]>(
      () => (response?.reports ?? []).map((entry) => ({ id: entry.id, entry })),
      [response?.reports]
    );
    const lastReadyRowsRef = useRef<ReportingTableRow[]>([]);
    if (!isLoadingRows) {
      lastReadyRowsRef.current = rows;
    }

    const empty = props.empty ?? { title: 'No entries found' };

    return (
      <Loading isLoading={isInitialLoad}>
        <div className={props.className}>
          {props.children}
          <div className="flex w-full items-center justify-between gap-2 px-[var(--page-margin)] pb-3">
            <div className="mr-auto flex items-center gap-2">
              {exactDateFilter ? (
                <TableDateFilter
                  value={
                    statementType
                      ? { fromDate: shiftDate(filterPeriodData.fromDate, 1), toDate: filterPeriodData.toDate }
                      : filterPeriodData
                  }
                  onChange={(value) =>
                    setFilterPeriodData(
                      statementType ? { fromDate: shiftDate(value.fromDate, -1), toDate: value.toDate } : value
                    )
                  }
                  disabledDays={{ after: new Date() }}
                />
              ) : (
                <TableMonthFilter
                  fromMonth={MONTH_FILTER_START}
                  value={filterPeriodData.toDate?.slice(0, 7)}
                  onChange={(value) => {
                    if (!value) {
                      setFilterPeriodData({ fromDate: undefined, toDate: undefined });
                      return;
                    }
                    const [year, month] = value.split('-').map(Number);
                    setFilterPeriodData(getMonthBoundaries(year, month - 1, filter?.category === 'statement'));
                  }}
                />
              )}

              {!bankAccountId && !hideAccountFilter && filter?.category === 'statement' && (
                <TableBankAccountFilter value={bankAccountIdFilterData} onChange={setBankAccountIdFilterData} />
              )}

              {typeOptions && (
                <TableSelectFilter
                  label="Type"
                  options={typeOptions}
                  value={typeFilterData}
                  onChange={setTypeFilterData}
                />
              )}

              {isFilterActive && (
                <Button
                  type="button"
                  variant="subtle-primary"
                  size="sm"
                  className="[&_svg]:[--icon-color:currentColor]"
                  onClick={handleFilterReset}
                >
                  Reset filters
                  <Icon.Reset />
                </Button>
              )}
            </div>
            {action}
          </div>
          <div className="px-[var(--page-margin)]">
            <DataTable<ReportingTableRow>
              {...tableDefaults}
              columns={columns}
              data={isLoadingRows ? lastReadyRowsRef.current : rows}
              isLoading={isLoading || isLoadingRows}
              rowKey={(row, index) => row.id ?? index}
              emptyContent={
                <TableEmpty
                  {...empty}
                  description={
                    isFilterActive ? 'No entries were found matching the specified filters' : empty.description
                  }
                  action={isFilterActive ? undefined : empty.action}
                />
              }
              onRowClick={props.onRowClick ? (row) => props.onRowClick?.(row.entry) : undefined}
            />
          </div>
          <TablePagination
            className="px-[var(--page-margin)] pb-6"
            pageSize={queryParams?.limit ?? 10}
            onPageSizeChange={(value: number) => {
              updateSettings({
                tableSettings: {
                  [props.tableId]: {
                    limit: value,
                  },
                },
              }).then(() => {
                setQueryParams(
                  {
                    startingAfter: undefined,
                    endingBefore: undefined,
                    limit: value,
                    page: 1,
                    ...defaultFilters,
                  },
                  true
                );
              });
            }}
            current={Number(queryParams?.page ?? 1)}
            hasPrevious={Number(queryParams?.page ?? 1) !== 1}
            hasNext={Boolean(response?.hasMore)}
            onPrevious={handlePrev}
            onNext={handleNext}
          />
        </div>
      </Loading>
    );
  }
);
